import React, { useEffect, useMemo, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ExternalLink } from "lucide-react";
import { supabase } from "@/lib/supabase";
import { navigateAdmin } from "@/lib/adminNav";
import {
  RideWiseRow,
  TaxiCallerRow,
  CrmAccountRow,
  CrmApprovalRow,
  CrmPermanentBookingRow,
} from "./types";
import {
  buildExpectedCostCode,
  extractTmClientNumberFromCostCode,
  fmtMoney,
  fmtNum,
  getCostCodePrefix,
  isTotalMobilityCostCode,
  normalizeClientNumber,
  safeArr,
} from "./helpers";

type CrmStatus = "matched" | "missing-crm" | "needs-review" | "duplicate" | "crm-only";
type CCStatus = "correct" | "missing" | "needs-review";
type ECardStatus = "matched" | "none" | "multiple";

interface ECardLite {
  id: string;
  card_last4: string | null;
  customer_first_name: string | null;
  customer_last_name: string | null;
  email: string | null;
  taxicaller_account_id: string | null;
  status: string | null;
}

interface ClientRow {
  clientNumber: string;
  ridewiseName: string;
  ridewiseFirstName: string;
  ridewiseSurname: string;
  crmAccountId: string | null;
  crmAccountName: string | null;
  crmTraveller: string | null;
  crmActive: boolean | null;
  approvalStatus: "current" | "expired" | "open" | "none";
  approvalFrom: string | null;
  approvalTo: string | null;
  hasPermanentBooking: boolean;
  trips: number;
  lastTrip: string;
  fareTotal: number;
  subsidy: number;
  hoist: number;
  expectedCostCode: string;
  taxicallerCostCode: string | null;
  crmStatus: CrmStatus;
  costCodeStatus: CCStatus;
  duplicateCount: number;
  isCrmOnly?: boolean;
  ecardStatus: ECardStatus;
  ecardMatches: ECardLite[];
}

interface Props {
  rw: RideWiseRow[];
  tc: TaxiCallerRow[];
  accounts: CrmAccountRow[];
  approvals: CrmApprovalRow[];
  permanentBookings: CrmPermanentBookingRow[];
  loading: boolean;
}

const norm = (s?: string | null) =>
  String(s || "").trim().toLowerCase().replace(/\s+/g, " ");

const TMClientCoverageTab: React.FC<Props> = ({
  rw, tc, accounts, approvals, permanentBookings, loading,
}) => {
  const [q, setQ] = useState("");
  const [filter, setFilter] = useState<
    "all" | "missing-crm" | "hoist" | "30d" | "90d" | "crm-only" | "expired" | "needs-review" | "ecard"
  >("all");

  // eCards loaded once; not date-filtered (small dataset, customer master)
  const [ecards, setEcards] = useState<ECardLite[]>([]);
  const [loadingEcards, setLoadingEcards] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoadingEcards(true);
      const { data, error } = await supabase
        .from("ecards")
        .select(
          "id, card_last4, customer_first_name, customer_last_name, email, taxicaller_account_id, status"
        );
      if (cancelled) return;
      if (error) {
        console.error("Failed to load eCards for TM coverage:", error);
        setEcards([]);
      } else {
        setEcards((data || []) as ECardLite[]);
      }
      setLoadingEcards(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Listen for cross-tab navigation events so opening from CRM / eCard
  // pre-fills the TM Coverage search box.
  useEffect(() => {
    const onNav = (e: Event) => {
      const detail = (e as CustomEvent<{ tab: string; search?: string }>).detail;
      if (detail?.tab === "fleetpulse" && detail.search) {
        setQ(detail.search);
        setFilter("all");
      }
    };
    window.addEventListener("app:admin-navigate", onNav);
    return () => window.removeEventListener("app:admin-navigate", onNav);
  }, []);



  // Build lookup indexes for eCards
  const ecardIndex = useMemo(() => {
    const byTcAccount = new Map<string, ECardLite[]>();
    const byEmail = new Map<string, ECardLite[]>();
    const byNamePair = new Map<string, ECardLite[]>(); // "first|last"
    for (const e of ecards) {
      const tc = norm(e.taxicaller_account_id);
      if (tc) {
        if (!byTcAccount.has(tc)) byTcAccount.set(tc, []);
        byTcAccount.get(tc)!.push(e);
      }
      const em = norm(e.email);
      if (em) {
        if (!byEmail.has(em)) byEmail.set(em, []);
        byEmail.get(em)!.push(e);
      }
      const np = `${norm(e.customer_first_name)}|${norm(e.customer_last_name)}`;
      if (np !== "|") {
        if (!byNamePair.has(np)) byNamePair.set(np, []);
        byNamePair.get(np)!.push(e);
      }
    }
    return { byTcAccount, byEmail, byNamePair };
  }, [ecards]);

  const data = useMemo(() => {
    // Index CRM accounts by numeric default_cost_code
    const accountsByNum = new Map<string, CrmAccountRow[]>();
    for (const a of safeArr(accounts)) {
      const num = extractTmClientNumberFromCostCode(a.default_cost_code);
      if (!num) continue;
      if (!accountsByNum.has(num)) accountsByNum.set(num, []);
      accountsByNum.get(num)!.push(a);
    }

    // Index approvals by numeric cost_code
    const approvalsByNum = new Map<string, CrmApprovalRow[]>();
    for (const a of safeArr(approvals)) {
      const num = extractTmClientNumberFromCostCode(a.cost_code);
      if (!num) continue;
      if (!approvalsByNum.has(num)) approvalsByNum.set(num, []);
      approvalsByNum.get(num)!.push(a);
    }
    for (const arr of approvalsByNum.values()) {
      arr.sort((a, b) => {
        if (!!b.is_current !== !!a.is_current) return b.is_current ? 1 : -1;
        return String(b.approval_to || "").localeCompare(String(a.approval_to || ""));
      });
    }

    const pbByAccount = new Map<string, boolean>();
    for (const p of safeArr(permanentBookings)) {
      if (p.account_id) pbByAccount.set(String(p.account_id), true);
    }

    const tcByNum = new Map<string, TaxiCallerRow[]>();
    for (const t of safeArr(tc)) {
      if (!isTotalMobilityCostCode(t.Cost_Code)) continue;
      const num = extractTmClientNumberFromCostCode(t.Cost_Code);
      if (!num) continue;
      if (!tcByNum.has(num)) tcByNum.set(num, []);
      tcByNum.get(num)!.push(t);
    }

    interface Agg {
      clientNumber: string;
      ridewiseFirstName: string;
      ridewiseSurname: string;
      ridewiseName: string;
      trips: number;
      lastTrip: string;
      fareTotal: number;
      subsidy: number;
      hoist: number;
    }
    const rwByNum = new Map<string, Agg>();
    for (const r of safeArr(rw)) {
      const num = normalizeClientNumber(r.client_number);
      if (!num) continue;
      const cur =
        rwByNum.get(num) ||
        ({
          clientNumber: num,
          ridewiseFirstName: r.client_first_name || "",
          ridewiseSurname: r.client_surname || "",
          ridewiseName:
            [r.client_first_name, r.client_surname].filter(Boolean).join(" ") || "",
          trips: 0, lastTrip: "", fareTotal: 0, subsidy: 0, hoist: 0,
        } as Agg);
      cur.trips += 1;
      cur.fareTotal += Number(r.fare_total || 0);
      cur.subsidy += Number(r.concession_amount || 0);
      if (r.hoist_deployed) cur.hoist += 1;
      if (r.trip_date && r.trip_date > cur.lastTrip) cur.lastTrip = r.trip_date;
      if (!cur.ridewiseFirstName) cur.ridewiseFirstName = r.client_first_name || "";
      if (!cur.ridewiseSurname) cur.ridewiseSurname = r.client_surname || "";
      if (!cur.ridewiseName) {
        cur.ridewiseName = [r.client_first_name, r.client_surname].filter(Boolean).join(" ");
      }
      rwByNum.set(num, cur);
    }

    const today = new Date().toISOString().slice(0, 10);

    const findEcardMatches = (
      agg: Agg | null,
      acc: CrmAccountRow | null
    ): ECardLite[] => {
      const seen = new Map<string, ECardLite>();
      // 1) by taxicaller_account_id == CRM account_id
      if (acc?.account_id) {
        const m = ecardIndex.byTcAccount.get(norm(acc.account_id)) || [];
        for (const e of m) seen.set(e.id, e);
      }
      // 2) by email (CRM traveller_email or account email is not in our row;
      //    we only have account_name + traveller_full_name)
      // 3) by traveller_full_name → first/last
      if (acc?.traveller_full_name) {
        const parts = String(acc.traveller_full_name).trim().split(/\s+/);
        if (parts.length >= 2) {
          const first = norm(parts[0]);
          const last = norm(parts.slice(-1)[0]);
          const m = ecardIndex.byNamePair.get(`${first}|${last}`) || [];
          for (const e of m) seen.set(e.id, e);
        }
      }
      // 4) by ridewise first+surname
      if (agg) {
        const first = norm(agg.ridewiseFirstName);
        const last = norm(agg.ridewiseSurname);
        if (first && last) {
          const m = ecardIndex.byNamePair.get(`${first}|${last}`) || [];
          for (const e of m) seen.set(e.id, e);
        }
      }
      return Array.from(seen.values());
    };

    const buildRow = (
      num: string,
      agg: Agg | null
    ): ClientRow => {
      const accs = accountsByNum.get(num) || [];
      const acc = accs[0] || null;
      const apprs = approvalsByNum.get(num) || [];
      const current = apprs.find((a) => a.is_current) || apprs[0] || null;
      let approvalStatus: ClientRow["approvalStatus"] = "none";
      if (current) {
        if (current.approval_is_open) approvalStatus = "open";
        else {
          const to = current.approval_to || "";
          approvalStatus = to && to >= today ? "current" : "expired";
        }
      }
      const tcMatches = tcByNum.get(num) || [];
      const tcCostCode = tcMatches[0]?.Cost_Code ?? null;

      const expected = buildExpectedCostCode(num, (agg?.hoist || 0) > 0);

      let crmStatus: CrmStatus;
      if (accs.length > 1) crmStatus = "duplicate";
      else if (!acc) crmStatus = !agg ? "crm-only" : "missing-crm";
      else if (
        agg && approvalStatus === "expired" && agg.trips > 0
      ) crmStatus = "needs-review";
      else crmStatus = "matched";

      let costCodeStatus: CCStatus;
      if (!tcCostCode) costCodeStatus = "missing";
      else {
        const tcNum = extractTmClientNumberFromCostCode(tcCostCode);
        const expectedPrefix = (agg?.hoist || 0) > 0 ? "TMH" : "TM";
        const actualPrefix = getCostCodePrefix(tcCostCode);
        if (tcMatches.length > 1) costCodeStatus = "needs-review";
        else if (tcNum !== num) costCodeStatus = "needs-review";
        else if (actualPrefix !== expectedPrefix) costCodeStatus = "needs-review";
        else costCodeStatus = "correct";
      }

      const ecardMatches = findEcardMatches(agg, acc);
      const ecardStatus: ECardStatus =
        ecardMatches.length === 0
          ? "none"
          : ecardMatches.length === 1
            ? "matched"
            : "multiple";

      return {
        clientNumber: num,
        ridewiseName: agg?.ridewiseName || "",
        ridewiseFirstName: agg?.ridewiseFirstName || "",
        ridewiseSurname: agg?.ridewiseSurname || "",
        crmAccountId: acc?.account_id || null,
        crmAccountName: acc?.account_name || null,
        crmTraveller: acc?.traveller_full_name || null,
        crmActive: acc?.is_active ?? null,
        approvalStatus,
        approvalFrom: current?.approval_from || null,
        approvalTo: current?.approval_to || null,
        hasPermanentBooking: !!(acc?.account_id && pbByAccount.get(acc.account_id)),
        trips: agg?.trips || 0,
        lastTrip: agg?.lastTrip || "",
        fareTotal: agg?.fareTotal || 0,
        subsidy: agg?.subsidy || 0,
        hoist: agg?.hoist || 0,
        expectedCostCode: expected,
        taxicallerCostCode: tcCostCode,
        crmStatus,
        costCodeStatus,
        duplicateCount: accs.length,
        isCrmOnly: !agg,
        ecardStatus,
        ecardMatches,
      };
    };

    const allNums = new Set<string>([
      ...rwByNum.keys(),
      ...accountsByNum.keys(),
      ...tcByNum.keys(),
    ]);

    const rows: ClientRow[] = [];
    for (const num of allNums) {
      rows.push(buildRow(num, rwByNum.get(num) || null));
    }
    rows.sort((a, b) => b.trips - a.trips);
    return rows;
  }, [rw, tc, accounts, approvals, permanentBookings, ecardIndex]);

  const filtered = useMemo(() => {
    const today = new Date();
    const ms = (days: number) => new Date(today.getTime() - days * 86400000).toISOString().slice(0, 10);
    const d30 = ms(30);
    const d90 = ms(90);
    return data.filter((r) => {
      if (q.trim()) {
        const s = q.toLowerCase();
        if (
          !r.clientNumber.includes(s) &&
          !(r.ridewiseName || "").toLowerCase().includes(s) &&
          !(r.crmAccountName || "").toLowerCase().includes(s) &&
          !(r.crmTraveller || "").toLowerCase().includes(s)
        ) return false;
      }
      switch (filter) {
        case "missing-crm": return r.crmStatus === "missing-crm";
        case "hoist": return r.hoist > 0;
        case "30d": return r.lastTrip >= d30;
        case "90d": return r.lastTrip >= d90;
        case "crm-only": return r.crmStatus === "crm-only";
        case "expired": return r.approvalStatus === "expired";
        case "needs-review": return r.crmStatus === "needs-review" || r.costCodeStatus === "needs-review" || r.crmStatus === "duplicate";
        case "ecard": return r.ecardStatus !== "none";
      }
      return true;
    });
  }, [data, q, filter]);

  const counts = useMemo(() => ({
    total: data.length,
    missing: data.filter((r) => r.crmStatus === "missing-crm").length,
    crmOnly: data.filter((r) => r.crmStatus === "crm-only").length,
    duplicate: data.filter((r) => r.crmStatus === "duplicate").length,
    review: data.filter((r) => r.crmStatus === "needs-review" || r.costCodeStatus === "needs-review").length,
    ccMissing: data.filter((r) => r.costCodeStatus === "missing" && r.trips > 0).length,
    ecard: data.filter((r) => r.ecardStatus !== "none").length,
  }), [data]);

  if (loading) {
    return (
      <Card className="bg-card border border-border">
        <CardContent className="py-12 text-center text-muted-foreground">Loading TM client coverage…</CardContent>
      </Card>
    );
  }

  const filterBtns: { v: typeof filter; l: string }[] = [
    { v: "all", l: `All (${counts.total})` },
    { v: "missing-crm", l: `Missing CRM (${counts.missing})` },
    { v: "crm-only", l: `CRM Only (${counts.crmOnly})` },
    { v: "needs-review", l: `Needs Review (${counts.review})` },
    { v: "ecard", l: `Has eCard (${counts.ecard})` },
    { v: "expired", l: "Expired Approvals" },
    { v: "hoist", l: "Hoist Clients" },
    { v: "30d", l: "Active 30d" },
    { v: "90d", l: "Active 90d" },
  ];

  // Build a search string for opening eCards: prefer last4 of first match,
  // fallback to last name then first name then traveller name.
  const buildEcardSearch = (r: ClientRow): string | null => {
    const first = r.ecardMatches[0];
    if (first) {
      if (first.card_last4) return first.card_last4;
      const fn = (first.customer_first_name || "").trim();
      const ln = (first.customer_last_name || "").trim();
      if (ln) return ln;
      if (fn) return fn;
    }
    if (r.ridewiseSurname) return r.ridewiseSurname;
    if (r.crmTraveller) {
      const parts = String(r.crmTraveller).trim().split(/\s+/);
      if (parts.length) return parts[parts.length - 1];
    }
    return null;
  };

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-6 gap-3">
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Clients</div><div className="text-xl font-bold">{fmtNum(counts.total)}</div></CardContent></Card>
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Missing CRM</div><div className="text-xl font-bold text-red-500">{fmtNum(counts.missing)}</div></CardContent></Card>
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">CRM Only</div><div className="text-xl font-bold text-blue-400">{fmtNum(counts.crmOnly)}</div></CardContent></Card>
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Duplicates</div><div className="text-xl font-bold text-purple-400">{fmtNum(counts.duplicate)}</div></CardContent></Card>
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">Cost Code Missing</div><div className="text-xl font-bold text-orange-400">{fmtNum(counts.ccMissing)}</div></CardContent></Card>
        <Card className="bg-card border border-border"><CardContent className="pt-4"><div className="text-xs text-muted-foreground">eCard Match</div><div className="text-xl font-bold text-emerald-400">{fmtNum(counts.ecard)}</div></CardContent></Card>
      </div>

      <Card className="bg-card border border-border">
        <CardHeader className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <CardTitle className="text-base">
              TM Client Coverage
              {loadingEcards && (
                <span className="ml-2 text-xs text-muted-foreground font-normal">
                  · loading eCards…
                </span>
              )}
            </CardTitle>
            <Input
              placeholder="Search client / account / name…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="dark-input max-w-xs"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {filterBtns.map((b) => (
              <Button
                key={b.v}
                size="sm"
                variant={filter === b.v ? "default" : "outline"}
                onClick={() => setFilter(b.v)}
              >
                {b.l}
              </Button>
            ))}
          </div>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead className="text-muted-foreground border-b border-border">
              <tr>
                <th className="text-left p-2">Client #</th>
                <th className="text-left p-2">RideWise Name</th>
                <th className="text-left p-2">CRM Account</th>
                <th className="text-left p-2">Traveller</th>
                <th className="text-left p-2">CRM Active</th>
                <th className="text-left p-2">Approval</th>
                <th className="text-left p-2">Perm Booking</th>
                <th className="text-right p-2">Trips</th>
                <th className="text-right p-2">Hoist</th>
                <th className="text-right p-2">Fare</th>
                <th className="text-right p-2">Subsidy</th>
                <th className="text-left p-2">Last Trip</th>
                <th className="text-left p-2">Expected CC</th>
                <th className="text-left p-2">TaxiCaller CC</th>
                <th className="text-left p-2">CRM Match</th>
                <th className="text-left p-2">Cost Code</th>
                <th className="text-left p-2">eCard</th>
                <th className="text-left p-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 1000).map((r) => {
                const rowClass =
                  r.crmStatus === "missing-crm" ? "bg-red-950/30" :
                  r.crmStatus === "crm-only" ? "bg-blue-950/30" :
                  r.crmStatus === "duplicate" ? "bg-purple-950/30" :
                  (r.crmStatus === "needs-review" || r.costCodeStatus === "needs-review") ? "bg-yellow-950/20" :
                  "";
                const ecardSearch = buildEcardSearch(r);
                const crmSearch = r.crmAccountId || r.crmAccountName || r.ridewiseName || r.clientNumber;
                return (
                  <tr key={r.clientNumber} className={`border-b border-border/50 ${rowClass}`}>
                    <td className="p-2 font-mono">{r.clientNumber}</td>
                    <td className="p-2">{r.ridewiseName || "—"}</td>
                    <td className="p-2">
                      {r.crmAccountId ? (
                        <span><span className="font-mono">{r.crmAccountId}</span> {r.crmAccountName ? `· ${r.crmAccountName}` : ""}</span>
                      ) : "—"}
                    </td>
                    <td className="p-2">{r.crmTraveller || "—"}</td>
                    <td className="p-2">
                      {r.crmActive == null ? "—" : (
                        <Badge className={r.crmActive ? "bg-green-700" : "bg-gray-600"}>
                          {r.crmActive ? "Active" : "Inactive"}
                        </Badge>
                      )}
                    </td>
                    <td className="p-2">
                      {r.approvalStatus === "current" && <Badge className="bg-green-700">Current{r.approvalTo ? ` → ${r.approvalTo}` : ""}</Badge>}
                      {r.approvalStatus === "open" && <Badge className="bg-blue-700">Open</Badge>}
                      {r.approvalStatus === "expired" && <Badge className="bg-red-700">Expired{r.approvalTo ? ` (${r.approvalTo})` : ""}</Badge>}
                      {r.approvalStatus === "none" && <span className="text-muted-foreground">—</span>}
                    </td>
                    <td className="p-2">
                      {r.hasPermanentBooking ? <Badge className="bg-emerald-700">Yes</Badge> : <span className="text-muted-foreground">No</span>}
                    </td>
                    <td className="p-2 text-right">{fmtNum(r.trips)}</td>
                    <td className="p-2 text-right">{fmtNum(r.hoist)}</td>
                    <td className="p-2 text-right">{fmtMoney(r.fareTotal)}</td>
                    <td className="p-2 text-right">{fmtMoney(r.subsidy)}</td>
                    <td className="p-2">{r.lastTrip || "—"}</td>
                    <td className="p-2 font-mono">{r.expectedCostCode || "—"}</td>
                    <td className="p-2 font-mono">{r.taxicallerCostCode || "—"}</td>
                    <td className="p-2">
                      {r.crmStatus === "matched" && <Badge className="bg-green-700">Matched</Badge>}
                      {r.crmStatus === "missing-crm" && <Badge className="bg-red-700">Missing CRM</Badge>}
                      {r.crmStatus === "crm-only" && <Badge className="bg-blue-700">CRM Only</Badge>}
                      {r.crmStatus === "needs-review" && <Badge className="bg-yellow-700">Needs Review</Badge>}
                      {r.crmStatus === "duplicate" && <Badge className="bg-purple-700">Duplicate ×{r.duplicateCount}</Badge>}
                    </td>
                    <td className="p-2">
                      {r.costCodeStatus === "correct" && <Badge className="bg-green-700">Correct</Badge>}
                      {r.costCodeStatus === "missing" && <Badge className="bg-red-700">Missing</Badge>}
                      {r.costCodeStatus === "needs-review" && <Badge className="bg-yellow-700">Needs Review</Badge>}
                    </td>
                    <td className="p-2">
                      {r.ecardStatus === "matched" && (
                        <Badge className="bg-emerald-700">
                          {r.ecardMatches[0]?.card_last4 ? `****${r.ecardMatches[0].card_last4}` : "Matched"}
                        </Badge>
                      )}
                      {r.ecardStatus === "multiple" && (
                        <Badge className="bg-emerald-800">×{r.ecardMatches.length}</Badge>
                      )}
                      {r.ecardStatus === "none" && (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="p-2">
                      <div className="flex gap-1">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[10px]"
                          onClick={() =>
                            navigateAdmin("crm", String(crmSearch || "").trim() || undefined)
                          }
                          title="Open this client in CRM Accounts"
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          CRM
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-6 px-2 text-[10px]"
                          disabled={!ecardSearch}
                          onClick={() =>
                            ecardSearch && navigateAdmin("ecard", ecardSearch)
                          }
                          title={
                            ecardSearch
                              ? `Open in eCards (search: ${ecardSearch})`
                              : "No name / card to search"
                          }
                        >
                          <ExternalLink className="w-3 h-3 mr-1" />
                          eCard
                        </Button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          {filtered.length > 1000 && (
            <div className="text-xs text-muted-foreground mt-2">
              Showing first 1000 of {fmtNum(filtered.length)} rows. Use filters / search to narrow down.
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default TMClientCoverageTab;
